/**
 * Tiny pure-CSS tooltip for info / help affordances.
 *
 * Renders a lucide HelpCircle icon (or the caller-supplied trigger)
 * that, on hover/focus, reveals a small dark popover with the help
 * text. No JS state, no portals — uses Tailwind `group-hover` on the
 * wrapping span so it can sit inline next to a heading or label.
 *
 * Usage:
 *   <InfoTooltip>Server-side events fire from our server to Meta…</InfoTooltip>
 *   <InfoTooltip side="bottom"><strong>Pinned</strong> pixel only…</InfoTooltip>
 */

import React from 'react';
import { HelpCircle } from 'lucide-react';

type Side = 'top' | 'bottom' | 'left' | 'right';

interface Props {
  children: React.ReactNode;
  /** Side of the trigger to anchor the popover. Defaults to 'top'. */
  side?: Side;
  /** Optional fixed width (Tailwind class). Defaults to w-64. */
  widthClassName?: string;
  /** Override the trigger; defaults to a HelpCircle icon. */
  trigger?: React.ReactNode;
  /** Extra classes for the trigger span. */
  className?: string;
}

const SIDE_CLASSES: Record<Side, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

export const InfoTooltip: React.FC<Props> = ({
  children,
  side = 'top',
  widthClassName = 'w-64',
  trigger,
  className = '',
}) => {
  return (
    <span className={`relative inline-flex group ${className}`}>
      <span
        tabIndex={0}
        aria-label="More info"
        className="inline-flex items-center justify-center text-gray-500 hover:text-gray-300 focus:text-gray-300 focus:outline-none cursor-help"
      >
        {trigger ?? <HelpCircle size={14} />}
      </span>
      <span
        role="tooltip"
        className={
          'pointer-events-none absolute z-50 ' +
          widthClassName +
          ' opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 ' +
          'transition-opacity duration-150 px-3 py-2 rounded-md text-xs leading-relaxed ' +
          'bg-gray-950 text-gray-100 border border-gray-700 shadow-xl ' +
          SIDE_CLASSES[side]
        }
      >
        {children}
      </span>
    </span>
  );
};
